import { useMemo } from "react";
import * as PropTypes from "prop-types";
import styled from "styled-components";
import moment from "moment";
import {
  getBeginOfDay,
  getDay,
  getEndOfDay,
  getMonth,
  isWeekend,
} from "../shared/dates";
import { tileProps } from "../shared/propTypes";
import ProgressBlock, { PROGRESS_HEIGHT, SIZE } from "./ProgressBlock";
import TreatmentHighlight from "./TreatmentHighlight";
import { isOutOfMax } from "../../../utils/Date";

export default function Day(props) {
  const {
    calendarType,
    classes,
    currentMonthIndex,
    date,
    highlightedValue,
    treatments,
    minDate,
    productOrders = [],
    value,
    ...otherProps
  } = props;

  const momentDate = moment(date);
  const momentValue = moment(value);
  const selected = momentDate.isSame(momentValue);
  const momentMinDate = moment(minDate);
  // See if this day has a timeSlot on it.
  const highlighted = highlightedValue.find(v =>
    moment(v.planned_date_time_from).isSame(momentDate, "day"),
  );
  // console.log("HIGHLIGHTED", highlightedValue);
  // See if this day has a treatment on it.
  let hasTreatment = false;
  if (!highlighted) {
    hasTreatment = !!treatments.find(t =>
      t.planned_date_time_from.isSame(momentDate, "day"),
    );
  }

  const hasProductOrder = useMemo(
    () => {
      const orders = productOrders.filter(order => {
        const delivery = moment.unix(order.deliveryDate);
        return delivery.isSame(momentDate, "day");
      });

      return orders.length > 0;
    },
    [productOrders, momentDate],
  );

  const isDateWeekend = isWeekend(date, calendarType);
  const disabled = momentDate.isBefore(moment(), "day");
  const day = getDay(date);

  const id = moment(date).format("DD-MM-YYYY");

  const maxRange = isOutOfMax(momentDate);

  const neighbourDays = getMonth(date) !== currentMonthIndex + 1;

  let content = (
    <DayContainer>
      <DayNumber selected={selected} highlighted={highlighted}>
        {day}
      </DayNumber>
    </DayContainer>
  );
  if (highlighted) {
    content = (
      <DayContainer>
        <ProgressBlock
          selected={selected}
          percent={highlighted.percent}
          highlighted={highlighted}
        >
          <DayNumber margin highlighted={highlighted}>
            {day}
          </DayNumber>
        </ProgressBlock>
        {hasProductOrder && <Day3D addProgress />}
      </DayContainer>
    );
  } else if (hasTreatment) {
    content = (
      <DayContainer>
        <TreatmentHighlight selected={selected}>
          <DayNumber highlighted selected={selected}>
            {day}
          </DayNumber>
        </TreatmentHighlight>
        {hasProductOrder && <Day3D />}
      </DayContainer>
    );
  } else if (hasProductOrder) {
    content = (
      <DayContainer>
        <TreatmentHighlight noBlue selected={selected}>
          <DayNumber highlighted selected={selected}>
            {day}
          </DayNumber>
        </TreatmentHighlight>
        {hasProductOrder && <Day3D />}
      </DayContainer>
    );
  }

  return (
    <ContainerButton
      disabled={disabled}
      planAdvance={maxRange}
      neighbourDays={neighbourDays}
      {...otherProps}
      onClick={e => {
        if (props.onClick) {
          e.persist();
          props.onClick(date, e);
        }
      }}
      highlighted={highlighted ? highlighted.percent : null}
      highlightedObject={!!highlighted || hasTreatment || hasProductOrder}
      date={date}
      // formatAbbr={formatLongDate}
      weekend={isDateWeekend}
      maxDateTransform={getEndOfDay}
      minDateTransform={getBeginOfDay}
      selected={selected}
      id={id}
      view="month"
    >
      {content}
    </ContainerButton>
  );
}

Day.propTypes = {
  ...tileProps,
  currentMonthIndex: PropTypes.number.isRequired,
  highlightedValue: PropTypes.array,
  treatments: PropTypes.array,
  productOrders: PropTypes.array,
};

Day.defaultProps = {
  highlightedValue: [],
  treatments: [],
};

function color({
  highlighted,
  theme,
  disabled,
  weekend,
  planAdvance,
  neighbourDays,
}) {
  if (highlighted && highlighted.maxGapMin < 50) {
    return weekend || theme.orange;
  }

  if (highlighted !== undefined && highlighted !== null) {
    return highlighted >= 50 ? theme.orange : theme.green;
  }
  return disabled || weekend || planAdvance || neighbourDays
    ? theme.disabledColor
    : theme.borderColor;
}

function opacity({ disabled, weekend, planAdvance, neighbourDays }) {
  return disabled || weekend || planAdvance || neighbourDays ? 0.4 : 1;
}

const ContainerButton = styled.button`
  position: relative;
  color: ${color} !important;
  overflow: visible !important;
  ${({ highlighted }) => highlighted !== null && "padding-top: 0 !important;"};
  cursor: ${({ disabled }) => (disabled ? "normal " : "pointer ")} !important;
  opacity: ${opacity} !important;
  margin: 0;

  ${({ theme, highlightedObject }) =>
    !highlightedObject &&
    `&:hover {
    background-color: ${theme.hoverColor};
  }`};

  ${({ selected, theme, highlightedObject }) =>
    selected &&
    !highlightedObject &&
    `background-color: ${theme.selectedColor}`};
`;

const DayContainer = styled.div`
  cursor: ${({ disabled }) => (disabled ? "normal " : "pointer ")} !important;
  position: relative;
`;

const Day3D = styled.div`
  width: 30px;
  height: ${({ addProgress = false }) => (addProgress ? `30px` : `24px`)};
  border: 1px solid black;
  position: absolute;
  border-radius: 4px;
  top: ${({ addProgress = false }) => (addProgress ? `-7px` : `-3px`)};
  left: calc(50% - 12px);
`;

const numberHeight = SIZE - PROGRESS_HEIGHT - 3;
const DayNumber = styled.span`
  ${({ theme, highlighted, selected }) =>
    highlighted || selected
      ? `font-weight: ${theme.fontWeight.semiBold}`
      : `font-weight: ${theme.fontWeight.light}`};

  width: 100%;
  ${({ margin }) =>
    margin && `height: ${numberHeight}px; line-height: ${numberHeight}px;`};
`;
